import React from "react";
import {
	Box,
	Chip,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import PhoneIphoneTwoToneIcon from "@mui/icons-material/PhoneIphoneTwoTone";
import { Link as RouterLink } from "react-router-dom";
import moment from "moment";
import type { IScanHistoryItem } from "../type/ticketScanType";

interface Props {
	scan: IScanHistoryItem;
	index: number;
	paginationSkip: number | string;
}

const getStatusChip = (status: string) => {
	const normalized = (status || "").toLowerCase();
	if (normalized === "approved" || normalized === "paid") {
		return <Chip label={status || "Paid"} color="success" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized.includes("pending")) {
		return <Chip label={status || "Pending"} color="warning" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized === "rejected" || normalized === "unpaid") {
		return <Chip label={status || "Unpaid"} color="error" size="small" sx={{ fontWeight: 600 }} />;
	}
	return <Chip label={status || "Active"} color="default" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
};

const ScanHistoryTableRow: React.FC<Props> = ({
	scan,
	index,
	paginationSkip,
}) => {
	const visitorFullName =
		scan.visitor_name ||
		`${scan.visitor_first_name || ""} ${scan.visitor_last_name || ""}`.trim() ||
		"Visitor";

	return (
		<TableRow hover key={scan.id || index}>
			{/* SL */}
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{index + 1 + Number(paginationSkip)}
				</Typography>
			</TableCell>

			{/* Scan Time */}
			<TableCell>
				<Typography variant="body2" fontWeight={500}>
					{scan.scan_time ? moment(scan.scan_time).format("LLL") : "-"}
				</Typography>
			</TableCell>

			{/* Ticket No */}
			<TableCell>
				<Chip
					component={scan.ticket_id ? RouterLink : "div"}
					to={scan.ticket_id ? `/visitor/ticket/${scan.ticket_id}` : undefined}
					clickable={Boolean(scan.ticket_id)}
					label={scan.ticket_no || `#${scan.ticket_id}`}
					size="small"
					color="primary"
					variant="outlined"
					sx={{ fontWeight: 600, cursor: scan.ticket_id ? "pointer" : "default" }}
				/>
			</TableCell>

			{/* Scan Type */}
			<TableCell>
				<Chip
					label={scan.scan_type || "IN"}
					size="small"
					color={scan.scan_type === "IN" ? "success" : "info"}
					variant="filled"
					sx={{ fontWeight: 700 }}
				/>
			</TableCell>

			{/* Visitor Info */}
			<TableCell>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
						{scan.visitor_id ? (
							<Typography
								component={RouterLink}
								to={`/visitor/details/${scan.visitor_id}`}
								variant="body2"
								fontWeight={600}
								color="primary.main"
								sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
							>
								{visitorFullName}
							</Typography>
						) : (
							<Typography variant="body2" fontWeight={600} color="text.primary">
								{visitorFullName}
							</Typography>
						)}
						{scan.visitor_no && (
							<Chip
								component={scan.visitor_id ? RouterLink : "div"}
								to={scan.visitor_id ? `/visitor/details/${scan.visitor_id}` : undefined}
								clickable={Boolean(scan.visitor_id)}
								label={scan.visitor_no}
								size="small"
								variant="outlined"
								sx={{
									height: 20,
									fontSize: "0.68rem",
									fontWeight: 600,
									borderRadius: "4px",
									borderColor: "grey.300",
									bgcolor: "grey.50",
									cursor: scan.visitor_id ? "pointer" : "default",
								}}
							/>
						)}
					</Box>

					{(scan.visitor_phone || scan.visitor_email) && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, mt: 0.2 }}>
							{scan.visitor_phone && (
								<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
									<PhoneIphoneTwoToneIcon
										sx={{ fontSize: 13, color: "primary.main", opacity: 0.85 }}
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}
									>
										{scan.visitor_phone}
									</Typography>
								</Box>
							)}
							{scan.visitor_email && (
								<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
									<EmailTwoToneIcon
										sx={{ fontSize: 13, color: "primary.main", opacity: 0.85 }}
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontSize: "0.75rem",
											lineHeight: 1.2,
											wordBreak: "break-word",
										}}
									>
										{scan.visitor_email}
									</Typography>
								</Box>
							)}
						</Box>
					)}
				</Box>
			</TableCell>

			{/* Organization & Designation */}
			<TableCell>
				<Typography variant="body2" fontWeight={600}>
					{scan.visitor_organization || "-"}
				</Typography>
				{scan.visitor_designation && (
					<Typography variant="caption" color="text.secondary">
						{scan.visitor_designation}
					</Typography>
				)}
			</TableCell>

			{/* Total Amount */}
			<TableCell>
				<Typography variant="body2" fontWeight={700} color="primary.main">
					{scan.total_amount ? `৳${scan.total_amount}` : "-"}
				</Typography>
			</TableCell>

			{/* Payment Status */}
			<TableCell>{getStatusChip(scan.payment_status || "paid")}</TableCell>

			{/* Scanned By */}
			<TableCell>
				<Typography variant="body2" fontWeight={600}>
					{scan.scan_by_name || `Admin #${scan.scan_by_id || "-"}`}
				</Typography>
				{scan.scan_by_email && (
					<Typography variant="caption" color="text.secondary">
						{scan.scan_by_email}
					</Typography>
				)}
			</TableCell>
		</TableRow>
	);
};

export default ScanHistoryTableRow;
